/**
 * useVoiceDumpStore.js — Zustand store for the Voice Dump wizard.
 *
 * State machine: idle → recording → processing → review → executing → done
 * Handles MediaRecorder lifecycle, Gemini API calls, action execution,
 * gamification rewards, and ADHD-friendly UX feedback.
 */

import { create } from 'zustand';
import { processVoiceDump, buildDataContext, BUILTIN_TEMPLATES } from '../utils/geminiService';
import { executeAction, ACTION_EXECUTION_ORDER } from '../utils/actionExecutor';
import useAICreditStore from './useAICreditStore';
import useTaskStore from './useTaskStore';
import useProjectStore from './useProjectStore';
import useAreaStore from './useAreaStore';
import useResourceStore from './useResourceStore';
import useFlowStore from './useFlowStore';
import useTimerStore from './useTimerStore';
import useIdentityStore from './useIdentityStore';
import useGamificationStore from './useGamificationStore';
import useInboxStore from './useInboxStore';
import { storage } from '../utils/storage';
import { logEvent } from '../utils/telemetry';
import {
  uploadVoiceCapture,
  deleteVoiceCapture,
  savePendingCapture,
  deletePendingCapture,
  listPendingCaptures,
} from '../utils/audioStorage';

// ---------- Voice Dump Stats (persistent) ----------
const DUMP_STATS_KEY = 'voice_dump_stats';
function getDumpStats() {
  return storage.get(DUMP_STATS_KEY, { totalDumps: 0, totalActions: 0 });
}
function incrementDumpStats(actionCount) {
  const stats = getDumpStats();
  stats.totalDumps += 1;
  stats.totalActions += actionCount;
  storage.set(DUMP_STATS_KEY, stats);
  return stats;
}

// ---------- Template Helpers ----------
const CUSTOM_TEMPLATES_KEY = 'voice_dump_templates';
function getCustomTemplates() {
  return storage.get(CUSTOM_TEMPLATES_KEY, []);
}

// ---------- History Helpers ----------
const HISTORY_KEY = 'voice_dump_history';
const MAX_HISTORY = 20;
function getDumpHistory() {
  return storage.get(HISTORY_KEY, []);
}
function saveDumpHistory(history) {
  storage.set(HISTORY_KEY, history.slice(0, MAX_HISTORY));
}

// ---------- ADHD UX Helpers ----------
function triggerHaptic(pattern = [50]) {
  if (navigator.vibrate) navigator.vibrate(pattern);
}
function playChime(type = 'start') {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    gain.gain.value = 0.08;
    if (type === 'start') {
      osc.frequency.setValueAtTime(523, ctx.currentTime); // C5
      osc.frequency.setValueAtTime(659, ctx.currentTime + 0.1); // E5
    } else {
      osc.frequency.setValueAtTime(659, ctx.currentTime); // E5
      osc.frequency.setValueAtTime(784, ctx.currentTime + 0.1); // G5
    }
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.2);
    setTimeout(() => ctx.close(), 500);
  } catch { /* audio not available, ignore */ }
}

// ---------- Recording Safety ----------
/** Auto-stop cap, mirrors QuickCapture.jsx's voice capture (src/components/Layout/QuickCapture.jsx). */
const MAX_RECORDING_DURATION_S = 300;

// ---------- Capture backup bookkeeping (module-level — NOT store state) ----------
// captureIds the user has explicitly discarded (reRecord, a stalled manual
// retry, or a dump that finished processing before its backup did) while
// that backup was still uploading or awaiting its automatic retry.
//
// This deliberately lives OUTSIDE the Zustand store. openWizard()/
// closeWizard() reset the store's state wholesale (`set({ ...INITIAL_STATE,
// ... })`) any time the wizard opens or closes — and persistCapture()'s
// whole point is surviving exactly that (a backup must finish even if the
// wizard closes, or a brand-new dump starts, before it does). If "has this
// capture been superseded" lived in store state, every one of those resets
// would make an unrelated, still-valid backup look superseded too — either
// silently orphaning it (pre-fix) or, worse, actively deleting a recording
// nobody asked to discard. Keeping it here means the ONLY thing that can
// ever mark a capture superseded is an explicit call to discardInFlightCapture()
// below, regardless of what the store's own fields do in the meantime.
const _supersededCaptures = new Set();

/**
 * Marks a still-uploading (or awaiting-retry) capture as explicitly
 * discarded, so _attemptPersistCapture cleans up its Storage object instead
 * of linking (or relinking) it into the Inbox once it resolves. A no-op once
 * the backup has already resolved ('saved' / 'skipped' / 'idle') — those are
 * handled by their own inboxItemId cleanup at each call site instead.
 */
function discardInFlightCapture(captureId, status) {
  if (captureId && (status === 'uploading' || status === 'failed')) {
    _supersededCaptures.add(captureId);
  }
}

// Cancel functions for a captureId's scheduled automatic retry (the
// setTimeout or 'online' listener _scheduleCaptureRetry below set up),
// keyed by captureId. Lets retryPersistCapture() cancel a pending automatic
// retry before manually re-attempting the SAME capture, so the two can
// never race each other into uploading (and linking) the same recording
// twice. Entries remove themselves the moment the retry actually fires.
const _pendingRetryHandles = new Map();

function cancelScheduledRetry(captureId) {
  const cancel = _pendingRetryHandles.get(captureId);
  if (cancel) {
    cancel();
    _pendingRetryHandles.delete(captureId);
  }
}

const INITIAL_STATE = {
  // Wizard state
  isOpen: false,
  step: 'idle', // idle | recording | processing | review | executing | done | history
  error: null,

  // Recording
  audioBlob: null,
  recordingDuration: 0,
  mediaRecorder: null,
  analyserNode: null,
  audioContext: null,

  // Cloud Storage backup of the current recording, independent of AI
  // processing — see persistCapture(). This is what makes a dump recoverable
  // from the Inbox if transcription fails or the wizard closes before the
  // user acts on the results; previously the blob lived only in memory and
  // was discarded the moment the wizard reset.
  //   status: 'idle' | 'uploading' | 'saved' | 'failed' | 'skipped'
  //   reason: null | 'AUDIO_TOO_LARGE' | 'NOT_AUTHENTICATED' | 'UNKNOWN'
  audioPersist: { status: 'idle', inboxItemId: null, reason: null },
  // Id of the capture THIS wizard session is currently tracking, purely for
  // deciding whether to mirror an outcome into audioPersist above (see
  // _attemptPersistCapture's applyIfCurrent). Resetting this to null on
  // open/close/re-record is safe and intentional — it does NOT cancel or
  // orphan the backup itself; see _supersededCaptures above for what does.
  _captureId: null,

  // AI results
  transcript: '',
  actions: [],
  summary: '',
  coachingNudge: '',

  // Execution results
  executionResults: null,

  // Templates
  selectedTemplate: 'general',
  customTemplates: getCustomTemplates(),

  // History
  dumpHistory: getDumpHistory(),
  historySearch: '',
};

const useVoiceDumpStore = create((set, get) => ({
  ...INITIAL_STATE,

  // Count of backups recovered by recoverPendingCaptures() (see the bottom
  // of this file) since the app launched — deliberately NOT part of
  // INITIAL_STATE: it must survive openWizard()/closeWizard()'s resets so a
  // "we recovered N recording(s)" notice isn't wiped the moment the wizard
  // is opened or closed for something unrelated. No UI reads this today: a
  // future banner/toast (shown independently of the wizard, since a
  // recovery can complete before the user ever opens it) should watch this
  // value and, when it's > 0, tell the user their unsaved recording(s) were
  // found in the Inbox, then call dismissRecoveredNotice() to clear it.
  recoveredCaptureCount: 0,
  dismissRecoveredNotice: () => set({ recoveredCaptureCount: 0 }),

  // ---------- Wizard Lifecycle ----------

  openWizard: (modeHint) => {
    // Check AI availability (credits or BYOK key)
    const aiMode = useAICreditStore.getState().getAIMode('voice_dump');
    if (aiMode === 'none') {
      set({ isOpen: true, step: 'idle', error: 'NO_CREDITS_OR_KEY' });
      return;
    }
    // Validate mode hint against available templates
    const validMode = modeHint && BUILTIN_TEMPLATES.some(t => t.id === modeHint)
      ? modeHint
      : 'general';
    set({
      ...INITIAL_STATE,
      isOpen: true,
      step: 'recording',
      selectedTemplate: validMode,
    });
  },

  closeWizard: () => {
    const { mediaRecorder, audioContext, step } = get();
    
    // Telemetry: Track abandonment if closed before execution
    if (step === 'recording' || step === 'processing' || step === 'review') {
      logEvent('voice_dump_abandoned', { step_abandoned: step });
    }

    // Cleanup recording if active
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
    if (audioContext && audioContext.state !== 'closed') {
      audioContext.close();
    }
    // Preserve templates and history across wizard close
    const { selectedTemplate, customTemplates, dumpHistory } = get();
    set({ ...INITIAL_STATE, selectedTemplate, customTemplates, dumpHistory });
  },

  // ---------- Recording ----------

  startRecording: async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Set up audio analysis for waveform
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(stream);
      const analyserNode = audioContext.createAnalyser();
      analyserNode.fftSize = 256;
      source.connect(analyserNode);

      // Set up MediaRecorder
      const chunks = [];
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm',
      });

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(chunks, { type: mediaRecorder.mimeType });
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());

        set({ audioBlob, step: 'processing' });
        // Auto-trigger processing — uses the in-memory blob directly, so it
        // is unaffected by whether the Storage backup below succeeds.
        get().processAudio();
        // Back up the raw recording to Cloud Storage + the Inbox in the
        // background, so it survives even if transcription fails or the
        // wizard is closed before the user acts on the results. See
        // persistCapture() below for the full contract.
        get().persistCapture(audioBlob, mediaRecorder.mimeType);
      };

      mediaRecorder.start(100); // Collect chunks every 100ms
      triggerHaptic([30, 50, 30]); // Haptic: record started
      playChime('start');

      // Start duration timer
      const startTime = Date.now();
      const durationInterval = setInterval(() => {
        const state = get();
        if (state.step !== 'recording') {
          clearInterval(durationInterval);
          return;
        }
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        set({ recordingDuration: elapsed });
        // Auto-stop cap (mirrors QuickCapture.jsx) — an unbounded recording
        // risks exceeding storage.rules' voice-upload size ceiling. Stop the
        // recorder directly (no haptic/chime — this isn't a user action).
        if (elapsed >= MAX_RECORDING_DURATION_S) {
          clearInterval(durationInterval);
          if (mediaRecorder.state !== 'inactive') mediaRecorder.stop();
        }
      }, 1000);

      set({
        mediaRecorder,
        analyserNode,
        audioContext,
        step: 'recording',
        error: null,
        recordingDuration: 0,
      });
    } catch (error) {
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        set({ error: 'MIC_PERMISSION_DENIED' });
      } else {
        set({ error: `MIC_ERROR: ${error.message}` });
      }
    }
  },

  stopRecording: () => {
    const { mediaRecorder, recordingDuration } = get();

    // Minimum recording duration check
    if (recordingDuration < 2) {
      set({ error: 'RECORDING_TOO_SHORT' });
      return;
    }

    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      triggerHaptic([50]); // Haptic: record stopped
      playChime('stop');
      mediaRecorder.stop(); // This triggers onstop → processAudio
    }
  },

  reRecord: () => {
    const { audioContext, audioPersist, _captureId } = get();
    if (audioContext && audioContext.state !== 'closed') {
      audioContext.close();
    }
    // The take being discarded may already have a safety-net copy sitting in
    // the Inbox — clean it up rather than leaving an orphaned "Voice Capture"
    // item behind for a recording the user explicitly redid. If it's still
    // uploading (or awaiting its automatic retry) instead, mark it so that
    // whenever it DOES resolve, _attemptPersistCapture cleans up the Storage
    // object rather than resurrecting/duplicating an Inbox item for a take
    // the user already discarded.
    if (audioPersist.inboxItemId) {
      useInboxStore.getState().deleteInboxItem(audioPersist.inboxItemId);
    } else {
      discardInFlightCapture(_captureId, audioPersist.status);
    }
    set({
      step: 'recording',
      audioBlob: null,
      transcript: '',
      actions: [],
      summary: '',
      error: null,
      recordingDuration: 0,
      mediaRecorder: null,
      analyserNode: null,
      audioContext: null,
      executionResults: null,
      _captureId: null,
      audioPersist: { status: 'idle', inboxItemId: null, reason: null },
    });
  },

  // ---------- Cloud Storage backup (recoverability) ----------
  // Uploads the raw recording to Cloud Storage and drops a reference into
  // the Inbox, independent of AI transcription — see the field comment on
  // audioPersist in INITIAL_STATE for why this exists.
  persistCapture: async (audioBlob, mimeType) => {
    const captureId = crypto.randomUUID();
    set({ _captureId: captureId, audioPersist: { status: 'uploading', inboxItemId: null, reason: null } });
    await get()._attemptPersistCapture(captureId, audioBlob, mimeType);
  },

  /**
   * Core of the backup: durably saves the blob locally, attempts the
   * upload, and resolves the outcome (linked / cleaned-up-as-orphan /
   * retried / given up). Shared by BOTH a live recording just finishing
   * (persistCapture above) and a leftover capture resumed on this launch
   * (recoverPendingCaptures below) — the two differ only in `opts`.
   *
   * @param {string} captureId - stable id for this recording; also the
   *   Storage filename (see uploadVoiceCapture), so every retry of the same
   *   capture (in-session or resumed on a later launch) overwrites the same
   *   object instead of creating a new one each time.
   * @param {{label?: string, isRecovery?: boolean}} [opts]
   * @returns {Promise<'saved'|'superseded'|'skipped'|'failed'>}
   */
  _attemptPersistCapture: async (captureId, audioBlob, mimeType, opts = {}) => {
    const { label = 'Voice Capture', isRecovery = false } = opts;

    // Durable copy FIRST, before the network attempt — this is what lets
    // the recording survive the tab/app closing while the upload (or a
    // later retry) is still running. See recoverPendingCaptures() for what
    // resumes a leftover row on the next launch.
    await savePendingCapture(captureId, audioBlob, mimeType);

    // Only touch audioPersist (the CURRENT wizard session's UI-facing
    // status) if this captureId is still the one that session cares about —
    // openWizard()/closeWizard() may have moved on to a different session
    // (or none) since this attempt started, and that session's own status
    // must never be clobbered by an unrelated, older capture resolving late.
    const applyIfCurrent = (patch) => {
      if (get()._captureId === captureId) set(patch);
    };

    try {
      const url = await uploadVoiceCapture(audioBlob, mimeType, captureId);

      if (_supersededCaptures.delete(captureId)) {
        // Re-recorded (or otherwise explicitly discarded) while this upload
        // was already in flight — the object now exists in Storage but
        // nothing will ever reference it, and storage.rules has no expiry
        // for users/{uid}/voice/. Clean it up instead of leaving it there
        // forever.
        deleteVoiceCapture(url).catch(() => {});
        deletePendingCapture(captureId).catch(() => {});
        return 'superseded';
      }

      const item = useInboxStore.getState().addInboxItem(label, 'voice_dump', {
        contentType: 'voice',
        attachments: [{ type: 'audio', data: url, mimeType }],
      });
      // The Inbox item (written through to localStorage synchronously by
      // addInboxItem) is now the durable record — only drop the local
      // durable copy AFTER it exists, so a close in between leaves, at
      // worst, a redundant recovery attempt next launch instead of a
      // silent loss.
      deletePendingCapture(captureId).catch(() => {});
      applyIfCurrent({ audioPersist: { status: 'saved', inboxItemId: item.id, reason: null } });
      if (isRecovery) set((state) => ({ recoveredCaptureCount: state.recoveredCaptureCount + 1 }));
      return 'saved';
    } catch (err) {
      if (_supersededCaptures.delete(captureId)) {
        deletePendingCapture(captureId).catch(() => {});
        return 'superseded';
      }

      // AUDIO_TOO_LARGE / NOT_AUTHENTICATED are permanent for THIS attempt —
      // retrying immediately won't help, so no in-session retry is
      // scheduled (avoids a doomed loop).
      const permanent = err?.code === 'AUDIO_TOO_LARGE' || err?.code === 'NOT_AUTHENTICATED';
      applyIfCurrent({
        audioPersist: { status: permanent ? 'skipped' : 'failed', inboxItemId: null, reason: err?.code || 'UNKNOWN' },
      });

      if (err?.code === 'AUDIO_TOO_LARGE') {
        // The blob's size never changes — no future attempt, this session
        // or a later launch, can ever succeed. Keeping the durable copy
        // would only waste local disk quota forever.
        deletePendingCapture(captureId).catch(() => {});
        return 'skipped';
      }
      if (permanent) {
        // NOT_AUTHENTICATED — keep the durable copy. Reaching the wizard at
        // all requires being signed in (every route is behind <AuthGate>),
        // so this is a signed-out blip, not a lasting state:
        // recoverPendingCaptures() gets a fresh, presumably-authenticated
        // attempt at the next launch instead of hammering the current
        // (signed-out) session with retries.
        return 'skipped';
      }
      get()._scheduleCaptureRetry(captureId, audioBlob, mimeType, opts);
      return 'failed';
    }
  },

  /**
   * Automatic retry for a transient failure (offline, network hiccup) —
   * mirrors utils/storage.js's own online-event retry pattern for
   * consistency. Keeps retrying (5s, or on the next 'online' event) until it
   * succeeds or is superseded — the durable local copy means there is no
   * harm in trying for as long as it takes.
   */
  _scheduleCaptureRetry: (captureId, audioBlob, mimeType, opts) => {
    const retry = () => {
      _pendingRetryHandles.delete(captureId);
      window.removeEventListener('online', retry);
      if (_supersededCaptures.has(captureId)) return; // discarded — nothing to retry
      get()._attemptPersistCapture(captureId, audioBlob, mimeType, opts);
    };
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      window.addEventListener('online', retry);
      _pendingRetryHandles.set(captureId, () => window.removeEventListener('online', retry));
    } else {
      const timeoutId = setTimeout(retry, 5000);
      _pendingRetryHandles.set(captureId, () => clearTimeout(timeoutId));
    }
  },

  /** Manual retry hook for a future "we couldn't back this up" UI affordance. */
  retryPersistCapture: () => {
    const { audioBlob, mediaRecorder, audioPersist, _captureId } = get();
    if (!audioBlob || audioPersist.status === 'uploading' || audioPersist.status === 'saved') return;
    const mimeType = mediaRecorder?.mimeType || audioBlob.type || 'audio/webm';
    if (_captureId) {
      // Reuse the existing captureId — this is a retry of the SAME take, so
      // it should reuse the same Storage filename (see uploadVoiceCapture's
      // captureId param) and the same durable local copy, instead of
      // minting a fresh capture and leaving the old one to be redundantly
      // picked up by recoverPendingCaptures() later (most concretely: a
      // NOT_AUTHENTICATED "skipped" capture keeps its durable copy around
      // for exactly this). Cancel any automatic retry already scheduled for
      // it first, so the two attempts can't race and create a duplicate
      // Storage object + Inbox item.
      cancelScheduledRetry(_captureId);
      set({ audioPersist: { status: 'uploading', inboxItemId: null, reason: null } });
      get()._attemptPersistCapture(_captureId, audioBlob, mimeType);
      return;
    }
    get().persistCapture(audioBlob, mimeType);
  },

  /**
   * Resumes every capture backup that never finished — the upload (or its
   * retry) was still running when the tab/app closed, so nothing ever
   * confirmed whether the recording made it to Storage. Runs once,
   * automatically, as soon as this module loads (see the bottom of this
   * file) — every always-mounted surface that can open the wizard
   * (VoiceDumpWizard, CommandPalette, QuickCapture) imports this store, so
   * in practice this fires on next app launch with no user action needed.
   *
   * Each survivor either uploads successfully — linked into the Inbox,
   * labeled "(recovered)" so it isn't a mystery item — or fails again and is
   * left for the NEXT launch to retry (see _attemptPersistCapture's
   * NOT_AUTHENTICATED handling for the one exception). Never throws: a
   * failure here must not block the rest of the app from loading.
   */
  recoverPendingCaptures: async () => {
    let pending;
    try {
      pending = await listPendingCaptures();
    } catch {
      return;
    }
    for (const entry of pending || []) {
      if (!entry?.id || !entry?.blob) continue;
      await get()._attemptPersistCapture(entry.id, entry.blob, entry.mimeType || 'audio/webm', {
        label: 'Voice Capture (recovered)',
        isRecovery: true,
      });
    }
  },

  // ---------- Processing ----------

  processAudio: async () => {
    const { audioBlob, selectedTemplate, customTemplates } = get();
    if (!audioBlob) {
      set({ error: 'No audio recorded', step: 'recording' });
      return;
    }

    // Resolve template prompt suffix
    const allTemplates = [...BUILTIN_TEMPLATES, ...customTemplates];
    const template = allTemplates.find(t => t.id === selectedTemplate);
    const templatePrompt = template?.promptSuffix || '';

    set({ step: 'processing', error: null });

    try {
      // Build context from all stores
      const dataContext = buildDataContext({
        tasks: useTaskStore.getState().tasks,
        projects: useProjectStore.getState().projects,
        areas: useAreaStore.getState().areas,
        resources: useResourceStore.getState().resources,
        flows: useFlowStore.getState().flows,
        identities: useIdentityStore.getState().identities,
        activeIdentity: useIdentityStore.getState().getActiveIdentity?.() || null,
        timerSettings: {
          focusDuration: useTimerStore.getState().focusDuration,
          restDuration: useTimerStore.getState().restDuration,
          reflectDuration: useTimerStore.getState().reflectDuration,
          prepDuration: useTimerStore.getState().prepDuration,
        },
      });

      // Determine AI mode (proxy or BYOK)
      const aiMode = useAICreditStore.getState().getAIMode('voice_dump');
      const result = await processVoiceDump(audioBlob, dataContext, templatePrompt, aiMode);

      set({
        transcript: result.transcript,
        actions: result.actions,
        summary: result.summary,
        coachingNudge: result.coachingNudge || '',
        step: 'review',
      });
    } catch (error) {
      const errorMessage = error.message || 'Unknown error';
      // Surface credit-specific errors for the UI
      if (errorMessage === 'INSUFFICIENT_CREDITS') {
        set({ error: 'INSUFFICIENT_CREDITS', step: 'idle' });
      } else {
        set({ error: errorMessage, step: 'review' });
      }
    }
  },

  // ---------- Template Actions ----------

  setTemplate: (templateId) => set({ selectedTemplate: templateId }),

  addCustomTemplate: (template) => {
    const { customTemplates } = get();
    const newTemplate = {
      ...template,
      id: template.id || crypto.randomUUID(),
      builtin: false,
    };
    const updated = [...customTemplates, newTemplate];
    storage.set(CUSTOM_TEMPLATES_KEY, updated);
    set({ customTemplates: updated });
  },

  deleteCustomTemplate: (templateId) => {
    const { customTemplates, selectedTemplate } = get();
    const updated = customTemplates.filter(t => t.id !== templateId);
    storage.set(CUSTOM_TEMPLATES_KEY, updated);
    set({
      customTemplates: updated,
      // Reset to general if deleted template was selected
      selectedTemplate: selectedTemplate === templateId ? 'general' : selectedTemplate,
    });
  },

  getAllTemplates: () => {
    const { customTemplates } = get();
    return [...BUILTIN_TEMPLATES, ...customTemplates];
  },

  // ---------- History Actions ----------

  showHistory: () => set({ step: 'history', historySearch: '' }),

  setHistorySearch: (query) => set({ historySearch: query }),

  getFilteredHistory: () => {
    const { dumpHistory, historySearch } = get();
    if (!historySearch.trim()) return dumpHistory;
    const q = historySearch.toLowerCase();
    return dumpHistory.filter(entry =>
      entry.transcript?.toLowerCase().includes(q) ||
      entry.summary?.toLowerCase().includes(q)
    );
  },

  replayDump: (historyId) => {
    const { dumpHistory } = get();
    const entry = dumpHistory.find(h => h.id === historyId);
    if (!entry) return;
    // Load past dump's actions into review step with all selected
    set({
      step: 'review',
      transcript: entry.transcript,
      actions: (entry.actions || []).map(a => ({ ...a, selected: true })),
      summary: entry.summary,
    });
  },

  // ---------- Review Actions ----------

  toggleAction: (actionId) => {
    set(state => ({
      actions: state.actions.map(a =>
        a.id === actionId ? { ...a, selected: !a.selected } : a
      ),
    }));
  },

  removeAction: (actionId) => {
    set(state => ({
      actions: state.actions.filter(a => a.id !== actionId),
    }));
  },

  editAction: (actionId, updates) => {
    set(state => ({
      actions: state.actions.map(a =>
        a.id === actionId ? { ...a, ...updates } : a
      ),
    }));
  },

  selectAll: () => {
    set(state => ({
      actions: state.actions.map(a => ({ ...a, selected: true })),
    }));
  },

  deselectAll: () => {
    set(state => ({
      actions: state.actions.map(a => ({ ...a, selected: false })),
    }));
  },

  // ---------- Execution ----------

  executeActions: async () => {
    const { actions } = get();
    const selectedActions = actions.filter(a => a.selected);

    if (selectedActions.length === 0) return;

    set({ step: 'executing' });

    // Entity creation chaining: sort so CREATE comes before UPDATE/DELETE,
    // and projects/areas come before tasks/resources to resolve dependencies.
    const sortedActions = [...selectedActions].sort((a, b) => {
      const oa = ACTION_EXECUTION_ORDER[a.type] ?? 10;
      const ob = ACTION_EXECUTION_ORDER[b.type] ?? 10;
      return oa - ob;
    });

    const results = { succeeded: [], failed: [] };

    for (const action of sortedActions) {
      try {
        await executeAction(action);
        results.succeeded.push(action);
      } catch (error) {
        results.failed.push({ action, error: error.message });
      }
    }

    // --- Achievements & stats ---
    // A brain-dump is entity CRUD, NOT focus work: it earns no focus XP and
    // must not advance the focus streak. Dump-count achievements still apply.
    if (results.succeeded.length > 0) {
      const gamification = useGamificationStore.getState();

      // Track dump stats and check achievements
      const dumpStats = incrementDumpStats(results.succeeded.length);
      gamification.checkAchievements({
        ...gamification.profile,
        totalVoiceDumps: dumpStats.totalDumps,
        level: gamification.getLevel(),
      });

      results.xpEarned = 0;
    }

    // --- Save to history ---
    const { transcript, actions: allActions, summary, selectedTemplate, dumpHistory } = get();
    const historyEntry = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      templateId: selectedTemplate,
      transcript,
      actions: allActions.map(a => ({ ...a })),
      executedActionIds: results.succeeded.map(a => a.id),
      skippedActionIds: allActions.filter(a => !a.selected).map(a => a.id),
      summary,
      xpEarned: results.xpEarned || 0,
      actionCount: results.succeeded.length,
    };
    const updatedHistory = [historyEntry, ...dumpHistory].slice(0, MAX_HISTORY);
    saveDumpHistory(updatedHistory);

    // The dump was fully processed and its actions applied — the transcript
    // and outcome are captured in history, so the raw-audio safety net in
    // the Inbox (see persistCapture()) is redundant now. Clean it up so a
    // successful dump doesn't leave a leftover "Voice Capture" item behind.
    // If the backup is still uploading (or awaiting its automatic retry)
    // instead — the user moved fast enough to finish reviewing before it
    // did — mark it the same way so it doesn't land a REDUNDANT item once
    // it resolves after the fact.
    const { audioPersist, _captureId } = get();
    if (audioPersist.inboxItemId) {
      useInboxStore.getState().deleteInboxItem(audioPersist.inboxItemId);
    } else {
      discardInFlightCapture(_captureId, audioPersist.status);
    }

    set({
      step: 'done',
      executionResults: results,
      dumpHistory: updatedHistory,
      audioPersist: { status: 'idle', inboxItemId: null, reason: null },
    });
  },
}));

// ---------- Action Executor (shared) ----------
// Uses the shared actionExecutor utility — see src/utils/actionExecutor.js

// Resume any capture backup that never finished (see recoverPendingCaptures()
// above for the full contract). Fired once, here, at module load rather than
// from a UI component: every always-mounted surface that can open the
// wizard imports this store, so this reliably runs on next app launch with
// no explicit wiring, and no dependence on any component existing at all.
useVoiceDumpStore.getState().recoverPendingCaptures();

export default useVoiceDumpStore;

